import { memo, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  MenuItem,
} from "@mui/material";
import { Close as CloseIcon } from "components/icons";
import MDBox from "components/MDBox";
import { createResource, updateResource } from "app/services/project.service";

const ROLES = ["Developer", "Tech Lead", "QA Engineer", "Project Manager", "Business Analyst", "Designer", "DevOps Engineer", "Architect"];

const initialData = {
  id: null,
  memberId: "",
  memberName: "",
  projectId: "",
  projectName: "",
  role: "",
  allocationPercentage: 100,
  startDate: "",
  endDate: "",
  utilizationRate: 0,
};

const AddResourceDialog = ({ open, setOpen, onSuccess, editObject, projects = [], members = [] }) => {
  const [data, setData] = useState(initialData);
  const [message, setMessage] = useState("");
  const [messageOpen, setMessageOpen] = useState(false);
  const [severity, setSeverity] = useState("success");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (editObject) {
      setData({
        ...editObject,
        startDate: editObject.startDate?.substring(0, 10) || "",
        endDate: editObject.endDate?.substring(0, 10) || "",
      });
    } else {
      setData(initialData);
    }
    setMessageOpen(false);
  }, [editObject, open]);

  const validateForm = () => {
    if (!data.projectId) {
      setError("Project is required");
      return false;
    }
    if (!data.memberId) {
      setError("Member is required");
      return false;
    }
    if (!data.role || data.role.trim() === "") {
      setError("Role is required");
      return false;
    }
    return true;
  };

  const setError = (errorMessage) => {
    setMessage(errorMessage);
    setMessageOpen(true);
    setSeverity("error");
  };

  const setSuccess = (successMessage) => {
    setMessage(successMessage);
    setMessageOpen(true);
    setSeverity("success");
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      if (editObject) {
        await updateResource(data.id, data);
        setSuccess("Resource updated successfully");
      } else {
        await createResource(data);
        setSuccess("Resource allocated successfully");
      }
      setTimeout(() => {
        setData(initialData);
        setMessageOpen(false);
        setOpen(false);
        onSuccess && onSuccess();
      }, 1000);
    } catch (error) {
      setError(error.message || "An error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setData(initialData);
    setMessageOpen(false);
    setOpen(false);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editObject ? "Edit Resource Allocation" : "Allocate Resource"}</DialogTitle>
      <DialogContent>
        <MDBox my={2}>
          <TextField
            id="projectId"
            label="Project"
            fullWidth
            select
            variant="outlined"
            value={data.projectId}
            onChange={(e) => {
              const project = projects.find((p) => p.id === e.target.value);
              setData({ ...data, projectId: e.target.value, projectName: project?.projectName || "" });
            }}
            required
            disabled={!!editObject}
          >
            {projects.map((p) => (
              <MenuItem key={p.id} value={p.id}>{p.projectName}</MenuItem>
            ))}
          </TextField>
        </MDBox>
        <MDBox my={2}>
          <TextField
            id="memberId"
            label="Member"
            fullWidth
            select
            variant="outlined"
            value={data.memberId}
            onChange={(e) => {
              const member = members.find((m) => m.id === e.target.value);
              setData({ ...data, memberId: e.target.value, memberName: member ? `${member.firstName} ${member.lastName}` : "" });
            }}
            required
            disabled={!!editObject}
          >
            {members.map((m) => (
              <MenuItem key={m.id} value={m.id}>{`${m.firstName} ${m.lastName}`}</MenuItem>
            ))}
          </TextField>
        </MDBox>
        <MDBox my={2} display="flex" gap={2}>
          <TextField
            id="role"
            label="Role"
            fullWidth
            select
            variant="outlined"
            value={data.role}
            onChange={(e) => setData({ ...data, role: e.target.value })}
            required
          >
            {ROLES.map((r) => (
              <MenuItem key={r} value={r}>{r}</MenuItem>
            ))}
          </TextField>
          <TextField
            id="allocationPercentage"
            label="Allocation %"
            type="number"
            fullWidth
            variant="outlined"
            value={data.allocationPercentage}
            onChange={(e) => setData({ ...data, allocationPercentage: Math.max(0, parseInt(e.target.value) || 0) })}
            inputProps={{ min: 0, max: 200 }}
            helperText={data.allocationPercentage > 100 ? "Over-allocation!" : ""}
          />
        </MDBox>
        <MDBox my={2} display="flex" gap={2}>
          <TextField
            id="startDate"
            label="Start Date"
            type="date"
            fullWidth
            variant="outlined"
            value={data.startDate}
            onChange={(e) => setData({ ...data, startDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            id="endDate"
            label="End Date"
            type="date"
            fullWidth
            variant="outlined"
            value={data.endDate}
            onChange={(e) => setData({ ...data, endDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
        </MDBox>
        <Collapse in={messageOpen}>
          <Alert
            severity={severity}
            action={
              <IconButton size="small" onClick={() => setMessageOpen(false)}>
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {message}
          </Alert>
        </Collapse>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={isSubmitting} variant="contained">
          {editObject ? "Update" : "Allocate"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(AddResourceDialog);

